import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function About() {
  return (
    <>
      <SchemaOrg type="WebPage" data={{ name: 'jinman - 关于我们', description: '了解jinman品牌故事、发展历程、用户口碑与联系方式。jinman是您值得信赖的首选品牌。' }} />
      <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

      <div className="bg-background-50">
        <BrandIntro />
        <ReputationSection />
        <ContactSection />
        <FAQSection />
      </div>
    </>
  );
}

function BrandIntro() {
  return (
    <section className="py-14 md:py-20" aria-labelledby="about-heading">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <h1 id="about-heading" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-5">
                关于 jinman
              </h1>
              <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                <p>
                  <strong>jinman</strong> 诞生于对品质的不懈追求与对创新的无限热忱。自创立以来，我们始终秉持"让科技温暖生活"的品牌理念，致力于将前沿科技与人文设计完美融合，为全球用户打造卓越的产品体验。
                </p>
                <p>
                  从最初的智能硬件起步，jinman 逐步拓展至数字服务、生活美学和企业解决方案四大核心板块，构建起完整的品牌生态体系。如今，jinman 产品已远销全球 30+ 个国家和地区，累计服务超过 500 万用户。
                </p>
                <p>
                  我们相信，真正优秀的产品不仅要有卓越的性能，更要有温度、有灵魂。每一款 jinman 产品的背后，都凝聚着数百名工程师和设计师的心血与智慧。
                </p>
              </div>
            </div>
            <div className="h-72 md:h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20creative%20office%20workspace%20with%20natural%20lighting%2C%20wooden%20desks%20and%20elegant%20decor%2C%20clean%20cream%20and%20terracotta%20aesthetic%2C%20no%20people%2C%20professional%20editorial%20photography%2C%20warm%20inviting%20atmosphere&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="jinman 品牌办公环境"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-16 md:mt-20">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 text-center mb-10">
              发展历程
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { year: '2018', title: '品牌创立', desc: 'jinman 正式成立，首款智能硬件产品发布，迅速获得市场关注。' },
                { year: '2020', title: '业务拓展', desc: '推出数字服务板块，企业客户突破 500 家，覆盖行业持续扩大。' },
                { year: '2023', title: '全球化布局', desc: '产品进入 30+ 国际市场，全球用户突破 300 万，品牌影响力持续提升。' },
                { year: '2026', title: '生态完善', desc: '四大板块协同发展，用户突破 500 万，开启 jinman 生态新篇章。' },
              ].map((item) => (
                <div key={item.year} className="bg-background-100 rounded-lg p-5 md:p-6">
                  <span className="text-accent-500 font-heading text-lg">{item.year}</span>
                  <h3 className="text-sm font-semibold text-foreground-900 mt-2 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReputationSection() {
  const testimonials = [
    { name: '张伟', role: '科技创业者', content: 'jinman 的智能家居产品彻底改变了我对智能生活的认知。从安装到使用，每一个环节都体现了品牌的用心和专业。', initials: 'ZW', rating: 5 },
    { name: '李娜', role: '家庭用户', content: '作为一个不太懂科技的家庭主妇，jinman 的产品上手非常简单。客服团队也很耐心，遇到问题总能第一时间解决。', initials: 'LN', rating: 5 },
    { name: '王建国', role: '企业CTO', content: '我们公司采购了 jinman 的企业解决方案，从部署到培训都非常专业。数据安全方面做得尤其出色，完全符合我们的合规要求。', initials: 'WJ', rating: 5 },
    { name: '陈思雨', role: '独立设计师', content: 'jinman 的生活美学系列产品是我工作室的标配。简约的设计风格与卓越的品质完美融合，客户来访都赞不绝口。', initials: 'CS', rating: 5 },
    { name: '刘强', role: '零售店主', content: '使用 jinman 数据分析工具后，我们店铺的运营效率提升了不止一个档次。库存管理和客户分析都变得一目了然。', initials: 'LQ', rating: 4 },
    { name: '赵敏', role: '教育从业者', content: 'jinman 的产品让我们的智慧教室建设事半功倍。学生们对智能设备的接受度很高，教学效果显著提升。', initials: 'ZM', rating: 5 },
  ];

  return (
    <section id="reputation" className="bg-background-100 py-14 md:py-20" aria-labelledby="reputation-heading">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 id="reputation-heading" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              用户口碑与声誉
            </h2>
            <p className="text-sm text-foreground-600 max-w-xl mx-auto">
              jinman 用户的真实评价，见证品牌的品质与信赖
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-background-50 rounded-lg p-6">
                <div className="flex items-center gap-1 mb-3 text-primary-500">
                  {[...Array(t.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                    <span className="text-xs font-semibold text-accent-600">{t.initials}</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d9rd63knlsngrm4lmafg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setErrorMessage(msg);
        setFormState('error');
      }
    } catch {
      setErrorMessage('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  return (
    <section id="contact" className="py-14 md:py-20" aria-labelledby="contact-heading">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="contact-heading" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600">
              有任何问题或合作意向，欢迎随时与我们联系
            </p>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8">
            {formState === 'success' ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-900 mb-2">感谢您的留言！</h3>
                <p className="text-sm text-foreground-600">我们已收到您的信息，将在 1-2 个工作日内与您联系。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form" noValidate>
                {formState === 'error' && (
                  <div className="mb-4 p-3 bg-accent-100/50 border border-accent-300 rounded-md text-sm text-accent-700">
                    {errorMessage}
                  </div>
                )}
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label htmlFor="contact-name" className="text-xs text-foreground-700 font-medium">姓名 *</label>
                      <input
                        id="contact-name" name="name" type="text" required
                        className="bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className="text-xs text-foreground-700 font-medium">邮箱 *</label>
                      <input
                        id="contact-email" name="email" type="email" required
                        className="bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-subject" className="text-xs text-foreground-700 font-medium">主题</label>
                    <input
                      id="contact-subject" name="subject" type="text"
                      className="bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-message" className="text-xs text-foreground-700 font-medium">留言内容 *</label>
                    <textarea
                      id="contact-message" name="message" required maxLength={500} rows={5}
                      className="bg-background-50 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您想了解的内容（限500字）"
                    ></textarea>
                  </div>
                  <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-background-50 font-medium py-3 px-6 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer mt-2"
                  >
                    发送留言
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-mail-line"></i>
              </div>
              <p className="text-xs text-foreground-600">contact@jinman.com</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-phone-line"></i>
              </div>
              <p className="text-xs text-foreground-600">400-888-9999</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-map-pin-line"></i>
              </div>
              <p className="text-xs text-foreground-600">中国 · 深圳</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    { question: 'jinman 产品的售后服务如何？', answer: 'jinman 所有产品均享受一年质保服务，7×24 小时客服热线随时为您解答问题。如需维修或更换，请通过官网联系客服发起售后流程。' },
    { question: 'jinman 企业方案是否支持定制化？', answer: '当然可以。jinman 企业方案提供高度灵活的定制化服务，我们会有专属顾问与您沟通具体需求，量身打造最适合贵公司的数字化解决方案。' },
    { question: '如何成为 jinman 的合作伙伴？', answer: '我们欢迎各类合作伙伴加入 jinman 生态。您可以通过"联系我们"表单提交合作意向，我们的商务团队会在 3 个工作日内与您取得联系。' },
    { question: 'jinman 产品支持哪些支付方式？', answer: 'jinman 官网支持支付宝、微信支付、银联卡以及主流国际信用卡等多种支付方式，确保您的购物体验便捷安全。' },
    { question: '海外用户可以购买 jinman 产品吗？', answer: '可以。jinman 产品面向全球用户销售，我们提供国际物流配送服务。具体运费和配送时间根据目的地国家有所不同。' },
  ];

  return (
    <section className="bg-background-100 py-14 md:py-20" aria-labelledby="faq-heading">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 id="faq-heading" className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              常见问题
            </h2>
          </div>
          <div className="flex flex-col gap-4">
            {faqs.map((faq, idx) => (
              <details key={idx} className="bg-background-50 rounded-lg p-5 group cursor-pointer">
                <summary className="text-sm font-medium text-foreground-900 list-none flex items-center justify-between">
                  {faq.question}
                  <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                </summary>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
          <SchemaOrg
            type="FAQPage"
            data={{ questions: faqs }}
          />
        </div>
      </div>
    </section>
  );
}